import { useEffect, useMemo } from 'react'
import { message, Typography } from 'antd'
import styled from 'styled-components'
import { t } from 'i18next'
import { useSelector } from 'react-redux'

import { ENotificationTab } from '../configs'
import { NotificationTag } from '../notification-tag/NotificationTag'
import { themes } from '@theme'
import { StyledEmpty, StyledPagination, StyledSpin } from '@components'
import {
  getListNotificationsAction,
  getNotificationUnreadAmountAction,
  markAllNotificationsAsReadAction,
  markNotificationAsReadByIdAction,
  notificationActions,
  RootState,
  selectNotificationLoading,
  useAppDispatch,
} from '@redux'
import { IGetNotificationListRequestParam } from '@interfaces'

export const NotificationList = ({
  activeTab,
}: {
  activeTab: ENotificationTab
}) => {
  const dispatch = useAppDispatch()
  const {
    listNotifications,
    currentPage,
    limitNotifications,
    totalNotifications,
    unreadNotifications,
  } = useSelector((state: RootState) => state.notification)

  const getListNotificationLoading = useSelector((state: RootState) =>
    selectNotificationLoading(state, 'getListNotificationsAction')
  )

  const notificationParams: IGetNotificationListRequestParam = useMemo(() => {
    return {
      page: currentPage,
      limit: limitNotifications,
      ...(activeTab === ENotificationTab.NOTIFICATION_UNREAD && {
        isRead: false,
      }),
    }
  }, [activeTab, currentPage, limitNotifications])

  const getNotifications = async (
    params?: IGetNotificationListRequestParam
  ) => {
    if (!!getListNotificationLoading) return

    await dispatch(getListNotificationsAction(params))
  }

  const handleMarkAsReadNotification = async (
    notificationId: number,
    isRead: boolean
  ) => {
    if (isRead) return

    try {
      await dispatch(markNotificationAsReadByIdAction(notificationId))
      await getNotifications(notificationParams)
      await dispatch(getNotificationUnreadAmountAction())
    } catch (err: any) {
      message.error(err?.message)
    }
  }

  const handleMarkAllAsReadNotification = async () => {
    if (unreadNotifications <= 0) return

    try {
      await dispatch(markAllNotificationsAsReadAction())
      await getNotifications(notificationParams)
      await dispatch(getNotificationUnreadAmountAction())
    } catch (err: any) {
      message.error(err?.message)
    }
  }

  useEffect(() => {
    getNotifications(notificationParams)
  }, [notificationParams])

  return (
    <NotificationListStyled>
      <MarkAllReadStyled onClick={handleMarkAllAsReadNotification}>
        {t('notification:mark_all_as_read')}
      </MarkAllReadStyled>
      <ListNotificationWrapper>
        {getListNotificationLoading ? (
          <StyledSpin
            className="!absolute z-20 top-0 left-0 w-full h-full bg-blue-200 bg-opacity-20"
            size="large"
          />
        ) : null}
        {!!listNotifications?.length ? (
          listNotifications?.map((item) => (
            <NotificationTag
              notificationItem={item}
              onClickNotification={(notificationId, isRead) =>
                handleMarkAsReadNotification(notificationId, isRead)
              }
            />
          ))
        ) : (
          <StyledEmpty className="!m-auto" />
        )}
      </ListNotificationWrapper>
      <PaginationWrapper>
        <StyledPagination
          current={currentPage}
          pageSize={limitNotifications}
          total={totalNotifications}
          onChange={(page) => {
            dispatch(notificationActions.setCurrentPage(page))
          }}
          showSizeChanger={false}
        />
      </PaginationWrapper>
    </NotificationListStyled>
  )
}

const NotificationListStyled = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 75dvh;
`

const ListNotificationWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 66dvh;
  max-height: 66dvh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid ${themes.theme.light.colors.lightBlue};
  border-radius: 8px;
  padding: 8px 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${themes.theme.light.colors.active};
    border-radius: 999px;
  }
`

const MarkAllReadStyled = styled(Typography.Paragraph)`
  color: ${themes.theme.light.colors.infor};
  font-size: 14px;
  text-decoration: underline;
  text-align: end;
  margin-bottom: 4px !important;
  cursor: pointer;
`
const PaginationWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`
